import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classnames from 'classnames';
import {
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModal } from '../../actions';
import { showCollectionAddModal } from '../../actions/Collections';
import {
  setFileCommentsAnnotateMode,
  setSelectedFile,
  updateFile,
} from '../../actions/Files';

import { isLoggedIn } from '../../api/Auth';
import {
  APIcreateFilesGroup,
  APIconvertFile,
} from '../../api/Files';

import ProcessingSpinner from '../ProcessingSpinner';
import UpgradePlanButton from '../UpgradePlanButton';

import { userCan } from '../../helpers/Auth';
import { userHasFeature } from '../../helpers/Features';
import {
  canBeUsedImageEditor,
  fileImageEditorNeedsConvert,
  fileSupportsAnnotations,
  getFileExtension,
  isConvertableImage,
  pdfViewerV2,
} from '../../helpers/Files';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { upgradeModalIfNeeded } from '../../helpers/UpgradePlan';
import { canSeeComments } from '../../helpers/Comments';

const FileToolsRibbon = ({
  profile,
  file,
  fileVersion,
  fileEdition,
  share,
  sidebarTab,
  afterCreateEdition,
  afterCreateVersion,
  handleImageEditor,
  updateCommentsCount,
  handleAddToCollection,
  updateFileAction,
  changeCommentsAnnotateMode,
  commentsAnnotateMode,
  handleSidebarTab,
  switchVersion,
  switchEdition,
  readOnly,
  isPageOpenedFile,
}) => {
  const intl = useIntl();

  const [watermarkedChangeLoading, setWatermarkChangeLoading] = useState(false);
  const [htmlCodeCopied, setHtmlCodeCopied] = useState(false);
  const [converting, setConverting] = useState(false);

  const watermarkEnabled = !!profile?.client?.watermark_url;
  const hasFeatureImageEditor = userHasFeature('image-editor');
  const fileImageEditorSupported = canBeUsedImageEditor(file);
  const fileNeedsConvert = fileImageEditorNeedsConvert(file);
  const fileAnnotationsSupported = fileSupportsAnnotations(file);
  const showComments = canSeeComments();

  const handleAnnotateMode = () => {
    if (!fileAnnotationsSupported) return;

    if ((!userHasFeature('file-annotations') && !share) || (share && !share.allow_comments)) return;

    if (getFileExtension(file) === 'PDF' && pdfViewerV2()) return;

    const handleAnnotations = () => {
      if (!commentsAnnotateMode) {
        handleSidebarTab('comments');
      }

      changeCommentsAnnotateMode(!commentsAnnotateMode);
    };

    upgradeModalIfNeeded('file-annotations', () => handleAnnotations());
  };

  const handleChangeWatermarked = () => {
    if (!watermarkEnabled) return;

    setWatermarkChangeLoading(true);

    APIcreateFilesGroup({ type: 'edit_files' }).then((response) => {
      const { data } = response;
      if (data) {
        const newFile = file;
        newFile.watermarked = !newFile.watermarked;
        newFile.uploadUuid = data.uuid;
        return updateFileAction(file).then(() => {
          setWatermarkChangeLoading(false);
        });
      }
      return null;
    });
  };

  const handleSwitchCurrentFile = () => {
    if (fileVersion) {
      switchVersion(null);
    }

    if (fileEdition && fileEdition.can_view_parent) {
      switchEdition(null);
    }
  };

  const handleOpenFileInWorkspace = () => {
    if (!file?.uuid) {
      return;
    }

    window.open(`/files/${file.uuid}`, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = () => {
    setHtmlCodeCopied(true);
    setTimeout(() => {
      setHtmlCodeCopied(false);
    }, 3000);

    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'HTML code copied' }),
    });
  };

  const handleClickImageEditor = (e) => {
    e.preventDefault();

    if (!fileImageEditorSupported) return;

    const handler = () => {
      if (
        !hasFeatureImageEditor ||
        converting || (
          !fileImageEditorSupported &&
          !isConvertableImage(file)
        )
      ) return;

      if (fileImageEditorSupported && !fileNeedsConvert) {
        handleImageEditor(
          file,
          fileVersion,
          afterCreateVersion,
          afterCreateEdition,
          updateCommentsCount,
        );
      } else {
        setConverting(true);
        APIconvertFile(file.uuid, { target: 'png' })
          .then((response) => {
            setConverting(false);
            handleImageEditor(
              {
                ...file,
                urls: {
                  ...file.urls,
                  converted: response.path || null,
                },
              },
              fileVersion,
              afterCreateVersion,
              afterCreateEdition,
              updateCommentsCount,
            );
          })
          .catch(() => {
            setConverting(false);
            addFlashMessage({
              type: 'danger',
              text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
            });
          });
      }
    };

    upgradeModalIfNeeded('image-editor', () => handler(), e);
  };

  const handleSwitchEditTranscription = () => {
    if (sidebarTab === 'transcription') {
      handleSidebarTab('details');
    } else {
      handleSidebarTab('transcription');
    }
  };

  if (!profile) return null;

  if (
    share &&
    !share.allow_comments &&
    (!share.allow_versions || (share.allow_versions && !fileVersion)) &&
    (!fileAnnotationsSupported || !share.allow_comments) &&
    !file.html_code
  ) {
    return null;
  }

  if (isPageOpenedFile && isLoggedIn()) {
    return (
      <div className="file-tools-ribbon-wrapper">
        <div className="file-tools-ribbon">
          <Button
            id="FileToolsRibbon-ViewPageFileInWorkspace"
            color="primary"
            onClick={handleOpenFileInWorkspace}
          >
            <span>{intl.formatMessage({ defaultMessage: 'View file in workspace' })}</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="file-tools-ribbon-wrapper">
      <div className="file-tools-ribbon">
        {!readOnly && (
          <>
            {(
              !share &&
              userCan('file.use_image_editor') && (
                userCan('file.download', file) ||
                (userCan('file.request_download', file) && file.request)
              )
            ) && (
              <>
                <Button
                  id="FileToolsRibbon-ImageEditor"
                  color="link"
                  onClick={handleClickImageEditor}
                  className={classnames({
                    disabled: !hasFeatureImageEditor ||
                      (!fileImageEditorSupported && !isConvertableImage(file)) ||
                      !fileImageEditorSupported ||
                      converting,
                  })}
                >
                  <i className={classnames('fal fa-crop fa-fw', { 'fa-flip': converting })} />
                  <span>
                    {converting ? (
                      intl.formatMessage({ defaultMessage: 'Preparing...' })
                    ) : (
                      intl.formatMessage({ defaultMessage: 'Image editor' })
                    )}
                  </span>
                  <UpgradePlanButton feature="image-editor" simple={true} />
                </Button>
                {(!(fileImageEditorSupported || isConvertableImage(file)) ||
                  !fileImageEditorSupported) && (
                  <UncontrolledTooltip
                    fade={false}
                    target="FileToolsRibbon-ImageEditor"
                    autohide={false}
                    flip={true}
                  >
                    <div>
                      {intl.formatMessage({ defaultMessage: 'File type not supported by Image Editor' })}
                    </div>
                  </UncontrolledTooltip>
                )}
              </>
            )}
            {!share && userCan('add_files_to_collections') && (
              <Button color="link" onClick={() => handleAddToCollection(file)}>
                <i className="fal fa-layer-plus fa-fw" />
                <span>{intl.formatMessage({ defaultMessage: 'Add to collection' })}</span>
              </Button>
            )}
            {(!share && userCan('file.edit', file) && file.can_be_watermarked) && (
              <>
                <Button
                  id="FileToolsRibbon-Watermark"
                  color="link"
                  className={classnames({ disabled: watermarkedChangeLoading || !watermarkEnabled || !userHasFeature('watermarking') })}
                  onClick={handleChangeWatermarked}
                >
                  {!watermarkedChangeLoading && (
                    <i className="fal fa-stamp fa-fw" />
                  )}
                  <ProcessingSpinner show={watermarkedChangeLoading} size="small" />
                  <span>
                    {(file.watermarked && watermarkEnabled) ?
                      intl.formatMessage({ defaultMessage: 'Remove watermark' }) :
                      intl.formatMessage({ defaultMessage: 'Add watermark' })}
                  </span>
                  <UpgradePlanButton feature="watermarking" simple={true} />
                </Button>
                {!watermarkEnabled && (
                  <UncontrolledTooltip
                    className="tools-ribbon-tooltip"
                    fade={false}
                    target="FileToolsRibbon-Watermark"
                    autohide={false}
                    flip={true}
                  >
                    <div>
                      {intl.formatMessage({ defaultMessage: 'Add a watermark in your <link>workspace settings</link>' }, {
                        link: (msg) => (
                          <Link to="/admin/workspace">
                            {msg}
                          </Link>
                        ),
                      })}
                    </div>
                  </UncontrolledTooltip>
                )}
              </>
            )}
            {showComments && (
              <>
                <Button
                  id="FileToolsRibbon-Annotate"
                  color="link"
                  className={classnames({
                    disabled: !fileAnnotationsSupported ||
                      (!userHasFeature('file-annotations') && !share) ||
                      (share && !share.allow_comments) ||
                      (getFileExtension(file) === 'PDF' && pdfViewerV2()),
                    active: fileAnnotationsSupported && commentsAnnotateMode,
                  })}
                  onClick={handleAnnotateMode}
                >
                  {commentsAnnotateMode ?
                    <i className="fas fa-comment-plus" /> :
                    <i className="fal fa-comment-plus" />}
                  <span>
                    {commentsAnnotateMode ?
                      intl.formatMessage({ defaultMessage: 'Cancel annotate' }) :
                      intl.formatMessage({ defaultMessage: 'Annotate' })}
                  </span>
                  <UpgradePlanButton feature="file-annotations" simple={true} />
                </Button>
                {!fileAnnotationsSupported && (
                  <UncontrolledTooltip
                    fade={false}
                    target="FileToolsRibbon-Annotate"
                    autohide={false}
                    flip={true}
                  >
                    <div>
                      {intl.formatMessage({ defaultMessage: 'File type not supported for Annotations' })}
                    </div>
                  </UncontrolledTooltip>
                )}
              </>
            )}
          </>
        )}
        {(fileVersion || (fileEdition && fileEdition.can_view_parent)) && (
          <>
            <Button
              id="FileToolsRibbon-SwitchVersion"
              color="primary"
              onClick={handleSwitchCurrentFile}
            >
              <i className="far fa-arrow-turn-left" />
              <span>{intl.formatMessage({ defaultMessage: 'View current file' })}</span>
            </Button>
            <UncontrolledTooltip
              fade={false}
              target="FileToolsRibbon-SwitchVersion"
              autohide={false}
              flip={true}
            >
              <div>
                {fileVersion && intl.formatMessage({ defaultMessage: `You're viewing a file version now` })}
                {fileEdition && intl.formatMessage({ defaultMessage: `You're viewing a file edition now` })}
              </div>
            </UncontrolledTooltip>
          </>
        )}
        {!readOnly && file.html_code && (
          <CopyToClipboard
            text={file.html_code}
            options={{
              format: 'text/plain',
            }}
            onCopy={handleCopyLink}
          >
            <Button
              id="FileToolsRibbon-CopyHtmlCode"
              color="link"
              onClick={handleSwitchCurrentFile}
            >
              {htmlCodeCopied ? (
                <i className="far fa-check" />
              ) : (
                <i className="far fa-code" />
              )}
              <span>{intl.formatMessage({ defaultMessage: 'Copy HTML code' })}</span>
            </Button>
          </CopyToClipboard>
        )}
        {(file.attributes.transcription && file.attributes.transcription !== 'error' && !share) && (
          <Button
            id="FileToolsRibbon-Transcription"
            color={sidebarTab === 'transcription' ? 'primary' : 'link'}
            disabled={file.attributes.transcription !== 'done' || !userHasFeature('search-video-content')}
            className={classnames({ disabled: file.attributes.transcription !== 'done' || !userHasFeature('search-video-content') })}
            onClick={handleSwitchEditTranscription}
          >
            <i className="far fa-subtitles" />
            <span>
              {file.attributes.transcription === 'done' ?
                intl.formatMessage({ defaultMessage: 'Transcription' }) :
                intl.formatMessage({ defaultMessage: 'Transcribing...' })}
            </span>
            <UpgradePlanButton feature="search-video-content" simple={true} />
          </Button>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  commentsAnnotateMode: state.Files.commentsAnnotateMode,
});

const mapDispatchToProps = dispatch => ({
  handleAddToCollection: (file) => {
    dispatch(setSelectedFile(file));
    dispatch(showCollectionAddModal());
  },
  handleImageEditor: (
    file,
    fileVersion,
    afterCreateVersion,
    afterCreateEdition,
    updateCommentsCount,
  ) => {
    dispatch(showModal('ImageEditorModal', {
      className: 'modal-image-editor',
      file,
      fileVersion,
      afterCreateVersion,
      afterCreateEdition,
      updateCommentsCount,
    }));
  },
  updateFileAction: file => (
    dispatch(updateFile(file))
  ),
  changeCommentsAnnotateMode: (commentsAnnotateMode) => (
    dispatch(setFileCommentsAnnotateMode(commentsAnnotateMode))
  ),
});

FileToolsRibbon.propTypes = {
  profile: PropTypes.object,
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  fileEdition: PropTypes.object,
  share: PropTypes.object,
  sidebarTab: PropTypes.string.isRequired,
  handleImageEditor: PropTypes.func.isRequired,
  afterCreateVersion: PropTypes.func.isRequired,
  afterCreateEdition: PropTypes.func.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
  handleAddToCollection: PropTypes.func.isRequired,
  updateFileAction: PropTypes.func.isRequired,
  changeCommentsAnnotateMode: PropTypes.func.isRequired,
  commentsAnnotateMode: PropTypes.bool.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  switchVersion: PropTypes.func.isRequired,
  switchEdition: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  isPageOpenedFile: PropTypes.bool,
};

FileToolsRibbon.defaultProps = {
  profile: null,
  fileVersion: null,
  fileEdition: null,
  share: null,
  readOnly: false,
  isPageOpenedFile: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(FileToolsRibbon);
